import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  deletePublicVideoComment,
  getInternalVideo,
  getPublicVideo,
  getPublicVideoComments,
  fetchVideoCaptions,
  getVideoChapters,
  getVideoPlayback,
  submitPublicVideoComment,
  updatePublicVideoComment,
} from "services/videoLibraryApi";
import {
  COMMENT_CATEGORIES,
  DEFAULT_COMMENT_CATEGORY,
  MAX_COMMENT_BODY_LENGTH,
  MAX_COMMENT_EMAIL_LENGTH,
  MAX_COMMENT_NAME_LENGTH,
  buildPublicCommentThread,
  characterLength,
  commentCategoryLabel,
  formatTimestamp,
  getPublicCommentSubmitError,
  isCommentsDisabledError,
  isPublicCommentRecord,
  storeCommenterToken,
  validateCommentDraft,
  validateViewerIdentity,
} from "helpers/publicVideoComments";
import VideoPlayer from "./VideoPlayer";

const VIEWER_STORAGE_KEY = "ce.videoViewer";

const readStoredViewer = () => {
  try {
    const raw = window.sessionStorage.getItem(VIEWER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.name === "string" &&
      parsed.name.trim() &&
      typeof parsed?.email === "string" &&
      parsed.email.trim()
      ? { name: parsed.name, email: parsed.email }
      : null;
  } catch {
    return null;
  }
};

const storeViewer = (viewer) => {
  try {
    window.sessionStorage.setItem(VIEWER_STORAGE_KEY, JSON.stringify(viewer));
  } catch {
    /* storage unavailable — the viewer simply re-prompts on reload */
  }
};

const clearStoredViewer = () => {
  try {
    window.sessionStorage.removeItem(VIEWER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const setPublicPageMetadata = (title) => {
  const previousTitle = document.title;
  const existingRobotsMeta = document.querySelector('meta[name="robots"]');
  const previousRobotsContent = existingRobotsMeta?.getAttribute("content");
  const robotsMeta = existingRobotsMeta || document.createElement("meta");

  robotsMeta.name = "robots";
  robotsMeta.content = "noindex, nofollow";
  if (!existingRobotsMeta) document.head.appendChild(robotsMeta);
  document.title = title;

  return () => {
    document.title = previousTitle;
    if (existingRobotsMeta) {
      if (previousRobotsContent === null) {
        existingRobotsMeta.removeAttribute("content");
      } else {
        existingRobotsMeta.setAttribute("content", previousRobotsContent);
      }
    } else {
      robotsMeta.remove();
    }
  };
};

const hasPublicVideoShape = (video) =>
  Boolean(
    video &&
      typeof video === "object" &&
      !Array.isArray(video) &&
      typeof video.title === "string" &&
      (typeof video.description === "string" || video.description === null) &&
      typeof video.commentsEnabled === "boolean" &&
      Array.isArray(video.languages) &&
      video.languages.length > 0,
  );

/**
 * Shown before anything about the video is rendered. A public link held by
 * someone who is not signed in must give a name and email first, and only then
 * may they see the video or its details.
 */
const ViewerGate = ({ onSubmit }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = (event) => {
    event.preventDefault();
    const { values, errors } = validateViewerIdentity({ name, email });
    setFieldErrors(errors);
    const firstInvalid = ["name", "email"].find((field) => errors[field]);
    if (firstInvalid) {
      document.getElementById(`viewer-gate-${firstInvalid}`)?.focus();
      return;
    }
    onSubmit(values);
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      disableEscapeKeyDown
      aria-labelledby="viewer-gate-title"
      sx={{ "& .MuiDialog-paper": { borderRadius: 3 } }}
    >
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle id="viewer-gate-title" sx={{ pb: 1 }}>
          Before you watch
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tell us who is watching so we can follow up on your feedback.
          </Typography>
          <Stack spacing={2}>
            <TextField
              id="viewer-gate-name"
              label="Your name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setFieldErrors((current) => ({ ...current, name: undefined }));
              }}
              autoComplete="name"
              autoFocus
              required
              fullWidth
              error={Boolean(fieldErrors.name)}
              helperText={fieldErrors.name || "As it will appear on your comment."}
              inputProps={{ maxLength: MAX_COMMENT_NAME_LENGTH }}
            />
            <TextField
              id="viewer-gate-email"
              label="Your email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setFieldErrors((current) => ({ ...current, email: undefined }));
              }}
              autoComplete="email"
              inputMode="email"
              required
              fullWidth
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email || "Never shown publicly."}
              inputProps={{
                maxLength: MAX_COMMENT_EMAIL_LENGTH,
                autoCapitalize: "none",
                spellCheck: false,
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button type="submit" variant="contained" fullWidth size="large">
            Watch video
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const PublicCommentForm = ({
  idPrefix,
  ariaLabel,
  bodyLabel,
  submitLabel,
  successMessage,
  failureMessage,
  commenterName,
  maxTimestampSeconds,
  languageCode = "en",
  prefillTimestamp,
  autoFocus = false,
  initialBody = "",
  initialCategory = DEFAULT_COMMENT_CATEGORY,
  initialTimestamp = "",
  onCancel,
  onSubmit,
  onError,
}) => {
  const [body, setBody] = useState(initialBody);
  const [category, setCategory] = useState(initialCategory);
  const [timestamp, setTimestamp] = useState(initialTimestamp);
  const [website, setWebsite] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (field, setter) => (event) => {
    setter(event.target.value);
    setFieldErrors((current) =>
      current[field] ? { ...current, [field]: undefined } : current,
    );
    setFeedback((current) => (current ? null : current));
  };

  const handleBodyFocus = () => {
    // Prefill from where the reviewer paused the video, unless they already
    // typed a timestamp of their own.
    if (
      timestamp.trim() === "" &&
      Number.isFinite(prefillTimestamp) &&
      prefillTimestamp >= 0
    ) {
      setTimestamp(formatTimestamp(prefillTimestamp));
      setFieldErrors((current) => ({ ...current, timestampSeconds: undefined }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const { values, errors } = validateCommentDraft(
      { body, category, timestampSeconds: timestamp },
      { bodyLabel, maxTimestampSeconds, languageCode },
    );
    setFieldErrors(errors);
    setFeedback(null);
    const firstInvalidField = ["timestampSeconds", "body"].find(
      (field) => errors[field],
    );
    if (firstInvalidField) {
      setFeedback({
        severity: "error",
        message: "Please correct the highlighted fields and try again.",
      });
      document.getElementById(`${idPrefix}-${firstInvalidField}`)?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({ ...values, website });
      setBody("");
      setCategory(DEFAULT_COMMENT_CATEGORY);
      setTimestamp("");
      setWebsite("");
      setFeedback({ severity: "success", message: successMessage });
    } catch (error) {
      setFeedback({
        severity: "error",
        message: getPublicCommentSubmitError(error, failureMessage),
      });
      if (onError) onError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" aria-label={ariaLabel} onSubmit={handleSubmit} noValidate>
      <Stack spacing={2}>
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            left: "-9999px",
            width: 1,
            height: 1,
            overflow: "hidden",
          }}
        >
          {/* Honeypot: real visitors never see or tab to this field. */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Commenting as <strong>{commenterName}</strong>
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <TextField
            id={`${idPrefix}-timestampSeconds`}
            label="Timestamp"
            value={timestamp}
            onChange={handleFieldChange("timestampSeconds", setTimestamp)}
            placeholder="1:23"
            size="small"
            // Shrunk so the label sits above the box like the category select
            // beside it, which keeps both fields on the same line.
            InputLabelProps={{ shrink: true }}
            error={Boolean(fieldErrors.timestampSeconds)}
            helperText={
              fieldErrors.timestampSeconds ||
              (Number.isSafeInteger(maxTimestampSeconds) && maxTimestampSeconds > 0
                ? `Optional · up to ${formatTimestamp(maxTimestampSeconds)}`
                : "Optional · mm:ss")
            }
            inputProps={{ maxLength: 12, inputMode: "numeric" }}
            sx={{ width: { xs: "100%", sm: 160 } }}
          />
          <TextField
            id={`${idPrefix}-category`}
            select
            label="What is this about?"
            value={category}
            onChange={handleFieldChange("category", setCategory)}
            size="small"
            inputProps={{ "aria-label": "Feedback category" }}
            helperText="Helps route the fix."
            sx={{ flex: 1, minWidth: 0 }}
          >
            {COMMENT_CATEGORIES.map((entry) => (
              <MenuItem key={entry.value} value={entry.value}>
                {entry.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <TextField
          id={`${idPrefix}-body`}
          label={`Your ${bodyLabel}`}
          value={body}
          onChange={handleFieldChange("body", setBody)}
          onFocus={handleBodyFocus}
          multiline
          minRows={3}
          required
          fullWidth
          autoFocus={autoFocus}
          error={Boolean(fieldErrors.body)}
          helperText={
            fieldErrors.body ||
            `${characterLength(body)} / ${MAX_COMMENT_BODY_LENGTH} characters`
          }
          inputProps={{ maxLength: MAX_COMMENT_BODY_LENGTH }}
        />
        {feedback && (
          <Alert
            severity={feedback.severity}
            role={feedback.severity === "error" ? "alert" : "status"}
          >
            {feedback.message}
          </Alert>
        )}
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          {onCancel && (
            <Button type="button" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? "Submitting…" : submitLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

/**
 * Own-comment actions sit behind a ⋮ menu so the card reads as a comment rather
 * than as a row of buttons. Owns its open state because hooks cannot run inside
 * the map that renders the comments.
 */
const CommentActionsMenu = ({ comment, isEditing, editFormId, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title="Comment actions">
        <IconButton
          size="small"
          aria-label={`Actions for the comment by ${comment.name}`}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ ml: "auto", flexShrink: 0 }}
        >
          <MoreVertRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            onEdit();
          }}
          aria-controls={isEditing ? editFormId : undefined}
        >
          {isEditing ? "Cancel edit" : "Edit comment"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            onDelete();
          }}
          sx={{ color: "error.main" }}
        >
          Delete comment
        </MenuItem>
      </Menu>
    </>
  );
};

const PublicVideoPage = ({ source = "public" }) => {
  const { publicId } = useParams();
  const { currentUser } = useAuth();
  const isPortalSource = source === "portal";

  const [viewer, setViewer] = useState(() => readStoredViewer());
  const [video, setVideo] = useState(null);
  const [commentThreads, setCommentThreads] = useState([]);
  const [videoLoading, setVideoLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [videoError, setVideoError] = useState("");
  const [commentsError, setCommentsError] = useState("");
  // Replies are a bare text field per top-level comment, so the draft lives here
  // keyed by comment id instead of in a toggled form.
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replySendingId, setReplySendingId] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [commentPendingDelete, setCommentPendingDelete] = useState(null);
  const [commentNotice, setCommentNotice] = useState("");
  // The list is the point of the panel, so the composer stays collapsed until the
  // viewer asks for it.
  const [composerOpen, setComposerOpen] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  // Playback assets are fetched per language: the signed MP4 URL is
  // time-limited and every language is its own Synthesia video.
  const [playback, setPlayback] = useState(null);
  const [playbackLoading, setPlaybackLoading] = useState(false);
  const [playbackError, setPlaybackError] = useState("");
  const [playbackKey, setPlaybackKey] = useState(0);
  const [languageCode, setLanguageCode] = useState("en");
  // Chapters resolve separately so playback never waits on them.
  const [chapters, setChapters] = useState([]);
  // Playback position lives here, not in the player: the player can remount when
  // a language loads, and would otherwise forget where the reviewer was.
  const playerStateRef = useRef({ time: 0, playing: false });
  const [resumePoint, setResumePoint] = useState(null);
  // Where the reviewer last paused — used to prefill the comment timestamp.
  const [pausedAt, setPausedAt] = useState(null);

  // A signed-in portal user is already identified; everyone else is gated.
  // A member record without an email cannot post a comment, so they are gated too.
  const signedInEmail = currentUser
    ? currentUser.email || currentUser.Email || ""
    : "";
  const signedInIdentity = currentUser && signedInEmail
    ? {
        name: currentUser.Name || currentUser.name || "",
        email: signedInEmail,
      }
    : null;
  const viewerIdentity = signedInIdentity || viewer;
  const needsIdentity = !viewerIdentity;

  const readVideo = isPortalSource && currentUser ? getInternalVideo : getPublicVideo;

  useEffect(() => {
    const title = video?.title
      ? `${video.title} | Communication Essentials`
      : "Communication Essentials Demo";
    return setPublicPageMetadata(title);
  }, [video?.title]);

  useEffect(() => {
    // Nothing is fetched — and so nothing about the video can leak — until the
    // visitor has identified themselves.
    if (needsIdentity) {
      setVideoLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let isCurrent = true;

    const loadVideo = async () => {
      setVideo(null);
      setCommentThreads([]);
      setVideoError("");
      setCommentsError("");
      setVideoLoading(true);
      setCommentsLoading(false);
      setReplyDrafts({});

      try {
        const response = await readVideo(publicId, {
          signal: controller.signal,
        });
        if (!isCurrent) return;
        const videoData = response.data?.video;
        if (!hasPublicVideoShape(videoData)) {
          setVideoError("This video is unavailable right now.");
          return;
        }

        setVideo(videoData);
        setVideoLoading(false);
        setCommentsLoading(true);

        try {
          const commentsResponse = await getPublicVideoComments(publicId, {
            signal: controller.signal,
          });
          const commentsData = commentsResponse.data?.comments;
          if (
            !Array.isArray(commentsData) ||
            commentsData.some((comment) => !isPublicCommentRecord(comment))
          ) {
            throw new Error("Unexpected approved comments response.");
          }
          if (isCurrent) {
            setCommentThreads(buildPublicCommentThread(commentsData));
          }
        } catch {
          if (controller.signal.aborted) return;
          setCommentsError("Approved comments could not be loaded.");
        } finally {
          if (isCurrent && !controller.signal.aborted) {
            setCommentsLoading(false);
          }
        }
      } catch (error) {
        if (!isCurrent) return;
        if (controller.signal.aborted) return;
        setVideoError(
          error?.response?.status === 401 || error?.response?.status === 403
            ? "This video is for signed-in members. Sign in to the Portal to watch it."
            : error?.response?.status === 404
              ? "This video is unavailable."
              : "This video could not be loaded. Please try again.",
        );
      } finally {
        if (isCurrent) setVideoLoading(false);
      }
    };

    loadVideo();

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [needsIdentity, publicId, readVideo, retryKey]);

  // Resolve playable assets for the selected language.
  useEffect(() => {
    if (!video || needsIdentity) return undefined;

    let isCurrent = true;
    const load = async () => {
      setPlaybackLoading(true);
      setPlaybackError("");
      try {
        const response = await getVideoPlayback(publicId, languageCode);
        const play = response.data?.play;
        if (!play || typeof play.url !== "string") {
          throw new Error("Unexpected playback response.");
        }
        if (!isCurrent) return;
        setPlayback({
          url: play.url,
          languageCode: play.languageCode,
          durationSeconds: play.durationSeconds ?? null,
          hasCaptions: Boolean(play.hasCaptions),
        });
      } catch {
        if (isCurrent) {
          setPlayback(null);
          setPlaybackError("This video could not be loaded. Please try again.");
        }
      } finally {
        if (isCurrent) setPlaybackLoading(false);
      }
    };

    load();
    return () => {
      isCurrent = false;
    };
  }, [video, needsIdentity, publicId, languageCode, playbackKey]);

  // Captions are fetched as raw VTT text and rendered by the player itself, so
  // there is no <track> and therefore no cross-origin or object-URL juggling.
  const [captionsText, setCaptionsText] = useState("");

  useEffect(() => {
    if (!playback?.hasCaptions) {
      setCaptionsText("");
      return undefined;
    }

    let isCurrent = true;
    setCaptionsText("");

    fetchVideoCaptions(publicId, playback.languageCode)
      .then((response) => {
        if (!isCurrent) return;
        const text = typeof response.data === "string" ? response.data : "";
        setCaptionsText(text);
      })
      .catch(() => {
        if (isCurrent) setCaptionsText("");
      });

    return () => {
      isCurrent = false;
    };
  }, [playback, publicId]);

  // Chapters are decoration: fetch them after playback is usable, and ignore
  // any failure rather than disturbing the player.
  useEffect(() => {
    if (!video || needsIdentity) return undefined;
    let isCurrent = true;
    setChapters([]);
    getVideoChapters(publicId, languageCode)
      .then((response) => {
        if (!isCurrent) return;
        const list = response.data?.chapters;
        setChapters(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (isCurrent) setChapters([]);
      });
    return () => {
      isCurrent = false;
    };
  }, [video, needsIdentity, publicId, languageCode]);

  const handleViewerIdentified = (values) => {
    storeViewer(values);
    setViewer(values);
  };

  const handleChangeViewer = () => {
    clearStoredViewer();
    setViewer(null);
  };

  // Snapshot the position before asking for another language, so the new source
  // can pick up where the reviewer left off.
  const handlePlayerLanguageChange = (code) => {
    setResumePoint({ ...playerStateRef.current });
    setPausedAt(null);
    setLanguageCode(code);
  };

  const identity = viewerIdentity || { name: "", email: "" };

  // Bound timestamps by the length of the language actually being watched:
  // translations differ in length, so a comment's valid range is per language.
  const activeLanguage = video?.languages?.find(
    (language) => language.code === languageCode,
  ) || video?.languages?.[0];
  const activeDurationSeconds =
    playback?.durationSeconds ?? activeLanguage?.durationSeconds ?? null;
  const activeMaxTimestamp =
    Number.isSafeInteger(activeDurationSeconds) && activeDurationSeconds > 0
      ? activeDurationSeconds
      : undefined;

  // A comment is bounded by the length of the language it was written against,
  // which may differ from the language currently playing.
  const maxTimestampForLanguage = (code) => {
    const language = video?.languages?.find((entry) => entry.code === code);
    if (Number.isSafeInteger(language?.durationSeconds) && language.durationSeconds > 0) {
      return language.durationSeconds;
    }
    return activeMaxTimestamp;
  };

  // Reloads the list without touching playback, so edit/delete does not restart
  // the video the reviewer is watching.
  const refreshComments = useCallback(async () => {
    try {
      const response = await getPublicVideoComments(publicId);
      const data = response.data?.comments;
      if (!Array.isArray(data) || data.some((comment) => !isPublicCommentRecord(comment))) {
        return;
      }
      setCommentThreads(buildPublicCommentThread(data));
      setCommentsError("");
    } catch {
      // A failed refresh keeps whatever is already on screen.
    }
  }, [publicId]);

  const handleCommentSubmit = useCallback(
    async (values) => {
      const response = await submitPublicVideoComment(publicId, {
        name: identity.name,
        email: identity.email,
        body: values.body,
        category: values.category,
        timestampSeconds: values.timestampSeconds,
        languageCode: values.languageCode,
        website: values.website,
      });
      // The token is only ever returned here, so keep it or the author loses the
      // ability to edit and delete this comment on a later visit.
      storeCommenterToken(publicId, response.data?.commenterToken);
      setCommentNotice("Comment posted.");
      setComposerOpen(false);
      await refreshComments();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [publicId, identity.name, identity.email, refreshComments],
  );

  const handleReplySend = async (comment) => {
    const draft = (replyDrafts[comment.id] || "").trim();
    if (!draft) return;
    setReplySendingId(comment.id);
    try {
      const response = await submitPublicVideoComment(publicId, {
        name: identity.name,
        email: identity.email,
        body: draft,
        category: "other",
        timestampSeconds: null,
        languageCode,
        website: "",
        parentCommentId: comment.id,
      });
      storeCommenterToken(publicId, response.data?.commenterToken);
      setReplyDrafts((current) => ({ ...current, [comment.id]: "" }));
      await refreshComments();
    } catch (error) {
      handleCommentSubmitError(error);
      setCommentNotice(
        getPublicCommentSubmitError(error, "Your reply could not be submitted."),
      );
    } finally {
      setReplySendingId(null);
    }
  };

  const handleCommentUpdate = async (commentId, values) => {
    await updatePublicVideoComment(publicId, commentId, {
      body: values.body,
      category: values.category,
      timestampSeconds: values.timestampSeconds,
    });
    setEditingComment(null);
    // The server puts an edited comment back in the moderation queue.
    setCommentNotice("Comment updated.");
    await refreshComments();
  };

  const handleCommentDelete = (comment) => setCommentPendingDelete(comment);

  const confirmCommentDelete = async () => {
    const comment = commentPendingDelete;
    if (!comment) return;
    try {
      await deletePublicVideoComment(publicId, comment.id);
      setCommentNotice("Comment deleted.");
      await refreshComments();
    } catch {
      setCommentNotice("That comment could not be deleted. Please try again.");
    } finally {
      setCommentPendingDelete(null);
    }
  };

  const handleCommentSubmitError = (error) => {
    if (!isCommentsDisabledError(error)) return;
    setVideo((current) =>
      current ? { ...current, commentsEnabled: false } : current,
    );
  };


  if (needsIdentity) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "grid",
          placeItems: "center",
          p: 2,
        }}
      >
        <ViewerGate onSubmit={handleViewerIdentified} />
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        py: { xs: 2, sm: 4 },
        px: { xs: 1.5, sm: 2 },
      }}
    >
      <Container maxWidth="xl">
        {videoLoading && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              minHeight: "60vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress aria-label="Loading video" />
            <Typography>Loading video…</Typography>
          </Box>
        )}

        {!videoLoading && videoError && (
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, textAlign: "center" }}>
            <Alert severity="error" role="alert">
              {videoError}
            </Alert>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 2 }}
            >
              <Button onClick={() => setRetryKey((value) => value + 1)}>
                Try again
              </Button>
              {/* An Internal Only link is indistinguishable from a missing one
                  until the caller is authenticated. */}
              {!currentUser && (
                <Button
                  component="a"
                  href={`/login?next=${encodeURIComponent(window.location.pathname)}`}
                  variant="contained"
                >
                  Sign in
                </Button>
              )}
            </Stack>
          </Paper>
        )}

        {!videoLoading && !videoError && video && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "minmax(0, 1fr) 380px",
              },
              columnGap: { xs: 2, md: 3 },
              rowGap: { xs: 2, sm: 3 },
              alignItems: "start",
            }}
          >
          <Box component="header" sx={{ gridColumn: "1 / -1" }}>
              <Typography
                component="p"
                variant="overline"
                color="primary.main"
                fontWeight={700}
              >
                Communication Essentials demonstration
              </Typography>
              <Typography
                component="h1"
                variant="h3"
                sx={{
                  mt: 0.5,
                  fontSize: { xs: "1.8rem", sm: "2.4rem" },
                  overflowWrap: "anywhere",
                }}
              >
                {video.title}
              </Typography>
              {video.description?.trim() && (
                <Typography
                  component="p"
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 1.5, whiteSpace: "pre-wrap" }}
                >
                  {video.description}
                </Typography>
              )}
            </Box>

            <Box
              component="section"
              aria-label="Video player"
              sx={{
                width: "100%",
                gridRow: { md: 2 },
                gridColumn: { md: 1 },
              }}
            >
              {playbackLoading && !playback && (
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    borderRadius: 2,
                    bgcolor: "common.black",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <CircularProgress aria-label="Loading video" />
                </Box>
              )}

              {playbackError && !playback && (
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    borderRadius: 2,
                    bgcolor: "common.black",
                    display: "grid",
                    placeItems: "center",
                    p: 2,
                  }}
                >
                  <Stack spacing={2} alignItems="center">
                    <Alert severity="error" role="alert">
                      {playbackError}
                    </Alert>
                    <Button onClick={() => setPlaybackKey((value) => value + 1)}>
                      Try again
                    </Button>
                  </Stack>
                </Box>
              )}

              {/*
                Kept mounted while a new language loads: the player holds the
                resume position, and unmounting it would reset to 0 on switch.
              */}
              {playback && (
                <Box sx={{ position: "relative" }}>
                  <VideoPlayer
                    src={playback.url}
                    title={`${video.title} player`}
                    captionsText={captionsText}
                    languageCode={languageCode}
                    languages={video.languages}
                    chapters={chapters}
                    resume={resumePoint}
                    onProgress={(time, playing) => {
                      playerStateRef.current = { time, playing };
                    }}
                    onPauseTime={setPausedAt}
                    onLanguageChange={handlePlayerLanguageChange}
                  />
                  {playbackLoading && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(0,0,0,0.45)",
                        borderRadius: 2,
                      }}
                    >
                      <CircularProgress aria-label="Loading language" />
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Paper
              component="section"
              aria-labelledby="public-video-comments-heading"
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
                gridRow: { md: 2 },
                gridColumn: { md: 2 },
                position: { md: "sticky" },
                top: { md: 24 },
                maxHeight: { md: "calc(100vh - 48px)" },
              }}
            >
              <Box>
                <Typography
                  id="public-video-comments-heading"
                  component="h2"
                  variant="h6"
                >
                  Your comments
                </Typography>
                {viewerIdentity && (
                  <Typography variant="caption" color="text.secondary">
                    Viewing as {identity.name}
                    {!signedInIdentity && (
                      <>
                        {" · "}
                        <Box
                          component="button"
                          type="button"
                          onClick={handleChangeViewer}
                          sx={{
                            border: 0,
                            p: 0,
                            background: "none",
                            font: "inherit",
                            color: "primary.main",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          Change
                        </Box>
                      </>
                    )}
                  </Typography>
                )}
              </Box>

              <Box sx={{ overflowY: { md: "auto" }, flex: { md: "1 1 auto" } }}>
                {commentsLoading && (
                  <Box role="status" aria-live="polite" sx={{ py: 1 }}>
                    <CircularProgress size={22} aria-label="Loading comments" />
                    <Typography component="span" sx={{ ml: 1 }}>
                      Loading comments…
                    </Typography>
                  </Box>
                )}

                {commentsError && (
                  <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                    {commentsError}
                  </Alert>
                )}

                {commentNotice && !commentsError && (
                  <Alert
                    severity="info"
                    role="status"
                    onClose={() => setCommentNotice("")}
                    sx={{ mb: 2 }}
                  >
                    {commentNotice}
                  </Alert>
                )}

                {!commentsLoading &&
                  !commentsError &&
                  commentThreads.length === 0 && (
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      You haven’t commented on this video yet.
                    </Typography>
                  )}

                {commentThreads.length > 0 && (
                  <Stack
                    component="ul"
                    spacing={1.5}
                    sx={{ listStyle: "none", p: 0, m: 0, mb: 2 }}
                  >
                    {commentThreads.map((comment) => {
                      const isEditing = editingComment?.id === comment.id;
                      const editFormId = `video-edit-${comment.id}-form`;

                      return (
                        <Paper
                          component="li"
                          variant="outlined"
                          key={comment.id}
                          sx={{ p: 1.5, overflowWrap: "anywhere" }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Typography component="h3" variant="subtitle2">
                              {comment.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={commentCategoryLabel(comment.category)}
                            />
                            {comment.languageCode && comment.languageCode !== "en" && (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="primary"
                                label={
                                  video.languages.find(
                                    (language) => language.code === comment.languageCode,
                                  )?.label || comment.languageCode.toUpperCase()
                                }
                              />
                            )}
                            {Number.isFinite(comment.timestampSeconds) && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={formatTimestamp(comment.timestampSeconds)}
                              />
                            )}
                            {comment.isOwn && (
                              <Chip size="small" color="info" label="You" />
                            )}
                            {comment.isAdmin && (
                              <Chip size="small" color="primary" label="Team" />
                            )}
                            {comment.resolved && (
                              <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                label="Done"
                              />
                            )}
                            {comment.isOwn && comment.editedAt && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label="Edited"
                              />
                            )}
                            {comment.isOwn && (
                              <CommentActionsMenu
                                comment={comment}
                                isEditing={isEditing}
                                editFormId={editFormId}
                                onEdit={() =>
                                  setEditingComment(isEditing ? null : comment)
                                }
                                onDelete={() => handleCommentDelete(comment)}
                              />
                            )}
                          </Stack>
                          <Typography
                            component="p"
                            variant="body2"
                            sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                          >
                            {comment.body}
                          </Typography>

                          {isEditing && (
                            <Box id={editFormId} sx={{ mt: 1.5 }}>
                              <PublicCommentForm
                                idPrefix={`video-edit-${comment.id}`}
                                ariaLabel={`Edit comment by ${comment.name}`}
                                bodyLabel="comment"
                                submitLabel="Save changes"
                                successMessage="Changes saved."
                                failureMessage="Your changes could not be saved. Please try again."
                                commenterName={comment.name}
                                maxTimestampSeconds={maxTimestampForLanguage(
                                  comment.languageCode,
                                )}
                                languageCode={comment.languageCode || "en"}
                                initialBody={comment.body}
                                initialCategory={
                                  comment.category || DEFAULT_COMMENT_CATEGORY
                                }
                                initialTimestamp={
                                  Number.isFinite(comment.timestampSeconds)
                                    ? formatTimestamp(comment.timestampSeconds)
                                    : ""
                                }
                                autoFocus
                                onCancel={() => setEditingComment(null)}
                                onSubmit={(values) =>
                                  handleCommentUpdate(comment.id, values)
                                }
                                onError={handleCommentSubmitError}
                              />
                            </Box>
                          )}

                          {(comment.replies.length > 0 ||
                            video.commentsEnabled === true) && (
                            <Stack
                              component="ul"
                              spacing={1}
                              aria-label={`Replies to ${comment.name}`}
                              sx={{
                                listStyle: "none",
                                p: 0,
                                mt: 1,
                                mb: 0,
                                pl: 1.5,
                                borderLeft: 3,
                                borderColor: "divider",
                              }}
                            >
                              {comment.replies.map((reply) => (
                                <Paper
                                  component="li"
                                  variant="outlined"
                                  key={reply.id}
                                  sx={{
                                    p: 1.25,
                                    bgcolor: "action.hover",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    <Typography
                                      component="h4"
                                      variant="subtitle2"
                                    >
                                      {reply.name}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={commentCategoryLabel(reply.category)}
                                    />
                                    {reply.isAdmin && (
                                      <Chip
                                        size="small"
                                        color="primary"
                                        label="Team"
                                      />
                                    )}
                                    {Number.isFinite(reply.timestampSeconds) && (
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={formatTimestamp(
                                          reply.timestampSeconds,
                                        )}
                                      />
                                    )}
                                  </Stack>
                                  <Typography
                                    component="p"
                                    variant="body2"
                                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                                  >
                                    {reply.body}
                                  </Typography>
                                </Paper>
                              ))}
                              {video.commentsEnabled === true && (
                                <Box component="li" sx={{ listStyle: "none" }}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                  >
                                    <TextField
                                      size="small"
                                      fullWidth
                                      multiline
                                      maxRows={4}
                                      placeholder={`Reply to ${comment.name}…`}
                                      value={replyDrafts[comment.id] || ""}
                                      onChange={(event) =>
                                        setReplyDrafts((current) => ({
                                          ...current,
                                          [comment.id]: event.target.value,
                                        }))
                                      }
                                      inputProps={{
                                        "aria-label": `Reply to ${comment.name}`,
                                        maxLength: MAX_COMMENT_BODY_LENGTH,
                                      }}
                                    />
                                    <Tooltip title="Post reply">
                                      <span>
                                        <IconButton
                                          onClick={() => handleReplySend(comment)}
                                          disabled={
                                            replySendingId === comment.id ||
                                            !(replyDrafts[comment.id] || "").trim()
                                          }
                                          aria-label={`Post reply to ${comment.name}`}
                                          sx={{ color: "primary.main" }}
                                        >
                                          {replySendingId === comment.id ? (
                                            <CircularProgress size={18} />
                                          ) : (
                                            <SendRoundedIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              )}
                            </Stack>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              {video.commentsEnabled === true ? (
                <Box
                  sx={{
                    borderTop: 1,
                    borderColor: "divider",
                    pt: 2,
                    mt: "auto",
                  }}
                >
                  {composerOpen ? (
                    <PublicCommentForm
                      idPrefix="video-comment"
                      ariaLabel="Leave a comment"
                      bodyLabel="comment"
                      submitLabel="Submit comment"
                      successMessage="Comment posted."
                      failureMessage="Your comment could not be submitted. Please try again."
                      commenterName={identity.name}
                      maxTimestampSeconds={activeMaxTimestamp}
                      languageCode={languageCode}
                      prefillTimestamp={pausedAt}
                      autoFocus
                      onCancel={() => setComposerOpen(false)}
                      onSubmit={handleCommentSubmit}
                      onError={handleCommentSubmitError}
                    />
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      fullWidth
                      onClick={() => setComposerOpen(true)}
                    >
                      Add a comment
                    </Button>
                  )}
                </Box>
              ) : (
                <Alert severity="info" role="status" sx={{ mt: "auto" }}>
                  Comments aren’t open yet.
                </Alert>
              )}
            </Paper>
          </Box>
        )}
      </Container>

      <Dialog
        open={Boolean(commentPendingDelete)}
        onClose={() => setCommentPendingDelete(null)}
        maxWidth="xs"
        aria-labelledby="delete-comment-title"
      >
        <DialogTitle id="delete-comment-title">Delete comment</DialogTitle>
        <DialogContent>
          <Typography>
            This removes your comment from the page. An administrator can still
            see it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCommentPendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmCommentDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const PublicVideoHostNotFound = () => {
  useEffect(
    () => setPublicPageMetadata("Video not found | Communication Essentials"),
    [],
  );

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "grid",
        placeItems: "center",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, textAlign: "center" }}>
          <Typography component="h1" variant="h4">
            Video not found
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            This video page is unavailable.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicVideoPage;
